import axios from "axios";

let base = process.env.REACT_APP_API_URL || "http://localhost:3001";
if (!base.endsWith("/api")) {
  base = base.replace(/\/+$/, "") + "/api";
}
const API_BASE_URL = base;

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Flag to track if refresh has failed
let refreshFailed = false;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Ensure cookies are sent
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Prevent infinite loop: don't retry refresh token calls, already retried requests, or if refresh has failed
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !isRefreshing &&
      !refreshFailed
    ) {
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await authService.refreshToken();
        isRefreshing = false;

        if (newToken) {
          api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // Refresh failed, mark as failed to prevent further attempts
          refreshFailed = true;
          throw new Error("Token refresh failed");
        }
      } catch (refreshError) {
        isRefreshing = false;
        refreshFailed = true;
        // Refresh failed, clear auth and redirect
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        return Promise.reject(error);
      }
    }

    // If refresh has failed and we get another 401, just redirect to login
    if (error.response?.status === 401 && refreshFailed) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

const authService = {
  async login(email, password) {
    try {
      const loginData = {
        email,
        password,
      };

      const response = await api.post("/auth/login", loginData);

      // Store tokens first
      this.setStoredAuth(response.data.token, null);

      // Store refresh token if provided
      if (response.data.refreshToken) {
        localStorage.setItem("refreshToken", response.data.refreshToken);
      }

      // Set the token in axios headers for subsequent requests
      api.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${response.data.token}`;

      // After login, fetch full user info
      const userInfo = await this.getUserInfo();
      this.setStoredAuth(response.data.token, userInfo);

      // Reset refresh failed flag on successful login
      refreshFailed = false;
      return { ...response.data, user: userInfo };
    } catch (error) {
      // Extract the actual error message from backend
      let errorMessage = "Login failed. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Throw error with the specific message
      throw new Error(errorMessage);
    }
  },

  async logout() {
    console.log("🔐 [AUTH SERVICE] Starting logout process...");

    try {
      const token = localStorage.getItem("token");
      if (token) {
        console.log("🌐 [AUTH SERVICE] Calling backend logout endpoint...");
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        console.log("✅ [AUTH SERVICE] Backend logout successful");
      } else {
        console.log(
          "⚠️ [AUTH SERVICE] No token found, skipping backend logout"
        );
      }
    } catch (error) {
      console.error("❌ [AUTH SERVICE] Backend logout error:", error);
      // Continue with local cleanup even if backend logout fails
    } finally {
      console.log("🧹 [AUTH SERVICE] Clearing local storage...");

      // Always clear local storage regardless of backend response
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("refreshToken");

      // Clear any other auth-related items
      localStorage.removeItem("darkMode");
      localStorage.removeItem("_grecaptcha");
      localStorage.removeItem("intercom.intercom-state-shwpcdf5");
      localStorage.removeItem("present-aokmtkt893@iemail.one");

      // Reset refresh failed flag on logout
      refreshFailed = false;

      // Clear axios default headers
      delete api.defaults.headers.common["Authorization"];

      console.log("✅ [AUTH SERVICE] Logout process completed");
    }
  },

  getStoredToken() {
    return localStorage.getItem("token");
  },

  getStoredUser() {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  },

  setStoredAuth(token, user) {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
  },

  adminRegister: async (userData) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/auth/admin-register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create admin account");
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  registerSystemAdmin: async (userData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/register-system-admin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || "Failed to create system administrator account"
        );
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  async refreshToken() {
    try {
      // Get refresh token from localStorage
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        console.error("No refresh token available");
        return null;
      }

      // Use a direct fetch call to avoid triggering the interceptor
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
        credentials: "include", // Include cookies
      });

      if (!response.ok) {
        console.error(`Refresh failed: ${response.status}`);
        return null;
      }

      const data = await response.json();
      const { accessToken } = data;

      if (accessToken) {
        localStorage.setItem("token", accessToken);
        return accessToken;
      }
      return null;
    } catch (error) {
      console.error("Refresh token error:", error);
      return null;
    }
  },

  async getUserInfo() {
    try {
      console.log("getUserInfo called");
      const token = localStorage.getItem("token");
      console.log("getUserInfo - Token exists:", !!token);

      const response = await api.get("/auth/me");
      console.log("getUserInfo - Response:", response.data);
      return response.data.user;
    } catch (error) {
      console.error("getUserInfo error:", error);
      console.error("getUserInfo error response:", error.response?.data);

      // Only logout if it's an authentication error (401)
      if (error.response?.status === 401) {
        console.log("Authentication failed, logging out");
        await this.logout();
      }

      throw error;
    }
  },

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem("token");
  },
};

export default authService;
export const getAuthToken = () => localStorage.getItem("token");
